import AvatarList from "@metacrm/metacrm-material-ui/dist/AvatarList";
import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import { Box } from "@mui/material";
import BigNumber from "bignumber.js";
import {
  formatDate,
  getTokenImageSrc,
  getChainList,
} from "features/helpers/utils";
import Address from "features/metadesk/components/Address";
import { cexList } from "features/configure";
import { selectEntityChainList } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { get, startCase } from "lodash-es";
import React from "react";
import { useSelector } from "react-redux";
import { useMappingLabel } from "../useMappingLabel";
import { useIntl } from "react-intl";

export default function useGetCDPTable(setTokens) {
  const { formatMessage } = useIntl();
  const chainList = getChainList();
  const { sourceMapping, lastEngagementMapping } = useMappingLabel();

  const headers = [
    {
      title: "User Identity",
      label: formatMessage({ id: "cdp.headline.identity" }),
      disabledSort: false,
      show: true,
      id: "userIdentity",
    },
    {
      title: "Wallet Total Value",
      label: formatMessage({ id: "cdp.headline.walletTotalValue" }),
      disabledSort: false,
      show: true,
      id: "walletTotalValue",
      width: 130,
    },
    {
      title: "Wallet Age",
      label: formatMessage({ id: "cdp.headline.walletAge" }),
      disabledSort: false,
      show: true,
      id: "walletAge",
    },
    {
      title: "Current Holdings",
      label: formatMessage({ id: "cdp.headline.holdings" }),
      disabledSort: false,
      show: true,
      id: "tokens",
      width: 80,
    },
    {
      title: "Active Chains",
      label: formatMessage({ id: "cdp.headline.activeChain" }),
      disabledSort: false,
      show: true,
      id: "activeChains",
      width: 80,
    },
    {
      title: "CEX Label(s)",
      label: formatMessage({ id: "cdp.headline.cex" }),
      disabledSort: false,
      show: true,
      id: "cexLabels",
      width: 80,
    },
    {
      title: "Source",
      label: formatMessage({ id: "cdp.headline.source" }),
      disabledSort: false,
      show: true,
      id: "source",
      width: 150,
    },
    {
      title: "Tags",
      label: formatMessage({ id: "cdp.headline.tags" }),
      disabledSort: true,
      show: true,
      id: "tags",
      width: 80,
    },
    {
      title: "Last Engagement",
      label: formatMessage({ id: "cdp.headline.lastEngagement" }),
      disabledSort: false,
      show: true,
      id: "lastEngagement",
      width: 150,
    },
  ];

  const renderLastEngagementType = (user) => {
    return lastEngagementMapping(user?.lastEngagement?.type);
  };

  const renderContents = (user) => {
    const userChainList = user.chainIds?.map((chainId) => {
      const item = chainList.find((chain) => chain.id === chainId);
      return {
        label: item?.name,
        src: item?.logo_url,
      };
    });

    const tokensList = user?.tokens?.token?.map((item) => ({
      label: item.name,
      src: getTokenImageSrc(item),
    }));

    const cexLabelList = user?.cexLabels?.map((item) => {
      const defaultCexIcon = require("assets/img/ic_cex.svg").default;
      const cexInfo = cexList.find((cex) => cex.id === item);

      return {
        label: cexInfo?.name || item,
        src: cexInfo ? cexInfo.icon : defaultCexIcon,
      };
    });

    let contents = [
      <div slot="userIdentity">
        <Address user={user} showExtra={true} />
      </div>,
      <div slot="walletTotalValue">
        {user?.walletTotalValue
          ? "$" + new BigNumber(user?.walletTotalValue).toFormat(0)
          : "-"}
      </div>,
      <div slot="walletAge">{user?.walletAge || "-"}</div>,
      <div slot="activeChains" onClick={(e) => e.stopPropagation()}>
        <AvatarList
          lists={userChainList}
          plusClick={(e) => {
            e.stopPropagation();
            setTokens(userChainList);
          }}
        />
      </div>,
      <div slot="tokens" onClick={(e) => e.stopPropagation()}>
        <AvatarList
          lists={tokensList}
          plusClick={(e) => {
            e.stopPropagation();
            setTokens(tokensList);
          }}
          fallbackSrc={require("assets/img/empty-token.svg").default}
        />
      </div>,
      <div slot="cexLabels">
        <AvatarList lists={cexLabelList} />
      </div>,
      <div slot="tags" onClick={(e) => e.stopPropagation()}>
        {user?.tags?.length > 0 ? (
          <>
            <Box display="flex" alignItems="center">
              {user?.tags?.slice(0, 2)?.map((tag) => (
                <MetaCRMTag
                  key={tag.value}
                  option={{
                    color: get(tag, "color", "#E6E6E6"),
                    label: tag.value,
                  }}
                />
              ))}
              {user?.tags?.length > 2 && (
                <Box color="#7b61ff" fontSize={12} ml="4px">
                  +{user.tags.length - 2}
                </Box>
              )}
            </Box>
          </>
        ) : (
          "-"
        )}
      </div>,
      <div slot="source">
        <Box>{sourceMapping(user?.source?.type) || "-"}</Box>
      </div>,
      <div slot="lastEngagement">
        <Box color="#555" fontSize="12px">
          {renderLastEngagementType(user)}
        </Box>
        <Box color="#A5A5A5" fontSize="12px">
          {user?.lastEngagement?.date
            ? formatDate(user.lastEngagement.date, { time: true })
            : "-"}
        </Box>
      </div>,
    ];
    return contents;
  };
  return {
    headers,
    renderContents,
  };
}
